import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Configurações de SEO por página
const pageSEOConfig = {
  '/': {
    title: 'PersonalNutri - Nutrição Inteligente com IA | Planos Personalizados',
    description: 'PersonalNutri - Sua jornada para uma vida mais saudável. Nutrição inteligente com IA, planos personalizados e acompanhamento completo.',
    keywords: 'nutrição, dieta, saúde, fitness, IA, inteligência artificial, personal nutri, nutricionista virtual, plano alimentar',
  },
  '/about': {
    title: 'Sobre o PersonalNutri | Nossa Missão e Equipe',
    description: 'Conheça o PersonalNutri: nossa missão é democratizar o acesso à nutrição de qualidade através de inteligência artificial avançada.',
    keywords: 'sobre personalnutri, missão, equipe, nutrição inteligente, história',
  },
  '/plans': {
    title: 'Planos e Preços | PersonalNutri',
    description: 'Escolha o plano ideal para você. PersonalNutri oferece opções gratuitas e premium com recursos exclusivos de nutrição personalizada.',
    keywords: 'planos personalnutri, preços, assinatura, premium, gratuito, nutrição',
  },
  '/nutrition': {
    title: 'Análise Nutricional com IA | PersonalNutri',
    description: 'Análise nutricional completa dos alimentos usando inteligência artificial. Descubra calorias, macros e micronutrientes instantaneamente.',
    keywords: 'análise nutricional, calorias, macros, proteína, carboidrato, gordura, vitaminas, minerais',
  },
  '/support': {
    title: 'Suporte e Ajuda | PersonalNutri',
    description: 'Central de ajuda do PersonalNutri. Encontre respostas para suas dúvidas, tutoriais e entre em contato com nossa equipe.',
    keywords: 'suporte personalnutri, ajuda, FAQ, contato, dúvidas, tutorial',
  },
  '/partnerships': {
    title: 'Parcerias e Afiliados | PersonalNutri',
    description: 'Torne-se um parceiro PersonalNutri. Programa de afiliados com comissões atrativas para influenciadores e profissionais de saúde.',
    keywords: 'parcerias personalnutri, afiliados, influenciadores, comissão, ganhar dinheiro',
  },
  '/privacy': {
    title: 'Política de Privacidade | PersonalNutri',
    description: 'Política de privacidade do PersonalNutri. Saiba como protegemos seus dados pessoais conforme a LGPD.',
    keywords: 'privacidade, LGPD, dados pessoais, proteção de dados, política',
  },
  '/terms': {
    title: 'Termos de Uso | PersonalNutri',
    description: 'Termos e condições de uso do aplicativo PersonalNutri. Leia antes de utilizar nossos serviços.',
    keywords: 'termos de uso, condições, contrato, serviço, regras',
  },
  '/policy': {
    title: 'Política de Cookies | PersonalNutri',
    description: 'Política de cookies do PersonalNutri. Entenda como usamos cookies para melhorar sua experiência.',
    keywords: 'cookies, política, rastreamento, experiência',
  },
  '/download': {
    title: 'Baixar App | PersonalNutri para iOS e Android',
    description: 'Baixe o PersonalNutri gratuitamente para iPhone e Android. Comece sua jornada de nutrição inteligente hoje.',
    keywords: 'download personalnutri, baixar app, iOS, Android, Google Play, App Store',
  },
};

/**
 * Hook para gerenciar SEO dinâmico por página
 * Atualiza title, description, canonical e Open Graph tags
 */
export const usePageSEO = (customConfig = {}) => {
  const location = useLocation();

  useEffect(() => {
    const pathname = location.pathname;
    const config = { ...pageSEOConfig[pathname], ...customConfig };
    
    if (!config.title) return;

    // Atualizar title
    document.title = config.title;

    // Atualizar meta description
    updateMetaTag('name', 'description', config.description);

    // Atualizar meta keywords
    if (config.keywords) {
      updateMetaTag('name', 'keywords', config.keywords);
    }

    // Atualizar canonical URL
    const canonicalUrl = `https://personalnutri.app${pathname === '/' ? '' : pathname}`;
    updateLinkTag('canonical', canonicalUrl);

    // Atualizar Open Graph tags
    updateMetaTag('property', 'og:title', config.title);
    updateMetaTag('property', 'og:description', config.description);
    updateMetaTag('property', 'og:url', canonicalUrl);

    // Atualizar Twitter tags
    updateMetaTag('name', 'twitter:title', config.title);
    updateMetaTag('name', 'twitter:description', config.description);
    updateMetaTag('name', 'twitter:url', canonicalUrl);

  }, [location.pathname, customConfig]);

  return null;
};

// Função auxiliar para atualizar meta tags
function updateMetaTag(attribute, name, content) {
  if (!content) return;
  
  let element = document.querySelector(`meta[${attribute}="${name}"]`);
  
  if (element) {
    element.setAttribute('content', content);
  } else {
    element = document.createElement('meta');
    element.setAttribute(attribute, name);
    element.setAttribute('content', content);
    document.head.appendChild(element);
  }
}

// Função auxiliar para atualizar link tags
function updateLinkTag(rel, href) {
  let element = document.querySelector(`link[rel="${rel}"]`);
  
  if (element) {
    element.setAttribute('href', href);
  } else {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    element.setAttribute('href', href);
    document.head.appendChild(element);
  }
}

export default usePageSEO;

